import React, { useEffect } from 'react';
import { CourseDataEntry } from './CourseAgendaOverview';
import {
	AccessorKeyColumnDef,
	ColumnFiltersState,
	ExpandedState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	Table as __Table,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import useTableState from '../../useTableState';
import { Table as BTable } from 'react-bootstrap';
import TableFooter from '../../Components/TableFooter';
import TableBody from '../../Components/TableBody';
import TableHeader from '../../Components/TableHeader';

type Props = {
	data: Partial<CourseDataEntry>[];
	columns: AccessorKeyColumnDef<Partial<CourseDataEntry>, unknown>[];
};
const tableName = 'course-agenda-table';

const Table = ({ data, columns }: Props) => {
	const { t } = useTranslation();
	const { setPageIndex, setPageSize, pageIndex, pageSize, filters, setFilters } =
		useTableState(tableName);

	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex,
		pageSize,
	});

	const [sorting, setSorting] = React.useState<SortingState>([
		{
			id: 'start',
			desc: false,
		},
	]);

	const [globalFilter, setGlobalFilter] = React.useState<string>('');
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(filters);

	const [expanded, setExpanded] = React.useState<ExpandedState>({});

	useEffect(() => {
		setPageIndex(pagination.pageIndex);
		setPageSize(pagination.pageSize);
	}, [pagination]);

	useEffect(() => {
		setFilters(columnFilters);
	}, [columnFilters]);

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		onColumnFiltersChange: setColumnFilters,
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		onExpandedChange: setExpanded,
		getSubRows: (row) => row.slots,
		enableGlobalFilter: true,
		onGlobalFilterChange: setGlobalFilter,
		getRowCanExpand: (row) => (row.original.slots?.length || 0) > 0,
		state: {
			pagination,
			sorting,
			globalFilter,
			columnFilters,
			expanded,
		},
		filterFromLeafRows: false,
	});

	return (
		<>
			<div className="d-flex justify-content-between">
				<div className="d-flex justify-content-start">
					{columnFilters.length > 0 ? (
						<button
							type="button"
							className="btn btn-outline-secondary text-nowrap ms-3"
							onClick={() => {
								setColumnFilters([]);
							}}>
							{t('table.clearFilters')}
						</button>
					) : null}
				</div>
				<div className="d-flex flex-row align-items-center">
					<label className="me-2">{t('table.search')}: </label>
					<input
						className="form-control form-control-sm d-inline-block mt-1"
						value={globalFilter}
						onChange={(e) => {
							setGlobalFilter(e.target.value);
						}}
					/>
				</div>
			</div>
			<BTable responsive hover size={'l'}>
				<TableHeader table={table as __Table<unknown>} />
				<TableBody table={table as __Table<unknown>} />
			</BTable>
			<TableFooter table={table as __Table<unknown>} />
		</>
	);
};

export default Table;
